import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { endpoints, paymentRequired, type MealPlan, type MealPlanEntry, type MealSlot, type Tier } from '../api/endpoints';
import { ApiException } from '../api/client';
import { Skeleton, ErrorState } from '../components/states';
import { SlotPickerModal } from '../components/SlotPickerModal';
import { GOALS, type Goal } from '../lib/goals';
import { UpgradeModal } from '../components/UpgradeModal';
import { track } from '../lib/analytics';

const DAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const SLOTS: { slot: MealSlot; label: string }[] = [
  { slot: 'breakfast', label: 'Завтрак' },
  { slot: 'lunch_soup', label: 'Обед · первое' },
  { slot: 'lunch_main', label: 'Обед · второе' },
  { slot: 'lunch_salad', label: 'Обед · салат' },
  { slot: 'dinner_main', label: 'Ужин · второе' },
  { slot: 'dinner_salad', label: 'Ужин · салат' },
];

export function MealPlanScreen() {
  const nav = useNavigate();
  const [plan, setPlan] = useState<MealPlan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [lowVariety, setLowVariety] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [picker, setPicker] = useState<{ dayIndex: number; slot: MealSlot } | null>(null);
  const [tier, setTier] = useState<Tier | null>(null);
  const [activeGoal, setActiveGoal] = useState<Goal | null>(null);
  const [upsell, setUpsell] = useState<{ requiredTier: 'pro' | 'family'; message: string } | null>(null);
  const [slots, setSlots] = useState<Set<MealSlot>>(() => new Set(SLOTS.map((s) => s.slot)));

  const load = useCallback(async () => {
    setError(null);
    setLowVariety(false); // баннер «мало вариантов» относится к авто-сбору; ручная правка/перезагрузка его снимает
    try {
      setPlan(await endpoints.getMealPlan());
    } catch (e) {
      setError(e instanceof ApiException ? e.body.message : 'Не удалось загрузить меню');
    }
  }, []);
  useEffect(() => {
    // Разовая загрузка на маунте (план без TanStack) — намеренный fetch-in-effect.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
  }, [load]);

  // Экран заранее тянет тариф (в отличие от FamilyScreen, полагающегося только на 402),
  // чтобы показать замок на направлениях до клика. Пока тариф НЕизвестен (tier===null —
  // грузится или сбой запроса) замок НЕ показываем: иначе Family увидит замок/ложный апселл
  // за то, что у него уже есть. Редкий клик не-Family до загрузки подстрахует серверный 402.
  useEffect(() => {
    void endpoints.getSubscription().then((s) => setTier(s.tier)).catch(() => setTier(null));
  }, []);
  const locked = tier !== null && tier !== 'family'; // замок только когда ТОЧНО не-Family

  const cell = (dayIndex: number, slot: MealSlot): MealPlanEntry | undefined =>
    plan?.entries.find((e) => e.dayIndex === dayIndex && e.slot === slot);

  function toggleSlot(slot: MealSlot) {
    setSlots((cur) => {
      const next = new Set(cur);
      if (next.has(slot)) {
        if (next.size > 1) next.delete(slot); // последний не убираем — иначе нечего собирать
      } else {
        next.add(slot);
      }
      return next;
    });
  }

  async function generate(goal?: Goal) {
    track('menu_generate_click', goal ? { goal } : undefined);
    setBusy(true);
    try {
      const slotsArg = slots.size < SLOTS.length ? SLOTS.filter((s) => slots.has(s.slot)).map((s) => s.slot) : undefined;
      const res = await endpoints.generateMealPlan(goal, slotsArg);
      setPlan({ weekStart: res.weekStart, entries: res.entries });
      setLowVariety(res.lowVariety);
      setActiveGoal(goal ?? null);
    } catch (e) {
      const pr = paymentRequired(e);
      if (pr) {
        setUpsell(pr);
        return;
      }
      setError(e instanceof ApiException ? e.body.message : 'Не удалось собрать неделю');
    } finally {
      setBusy(false);
    }
  }

  function onGoalClick(goal: Goal) {
    if (locked) {
      setUpsell({ requiredTier: 'family', message: 'Меню под цель доступно на Family' });
      return;
    }
    void generate(goal); // tier неизвестен или Family → пробуем; не-Family отлетит 402 → апселл в catch
  }

  async function pick(recipeId: string) {
    if (!picker) return;
    const { dayIndex, slot } = picker;
    setPicker(null);
    try {
      await endpoints.setMealEntry(dayIndex, slot, recipeId);
      await load();
    } catch (e) {
      setError(e instanceof ApiException ? e.body.message : 'Не удалось назначить блюдо');
    }
  }

  async function clearSlot(dayIndex: number, slot: MealSlot) {
    try {
      await endpoints.setMealEntry(dayIndex, slot, null);
      await load();
    } catch (e) {
      setError(e instanceof ApiException ? e.body.message : 'Не удалось очистить');
    }
  }

  async function reroll(dayIndex: number, slot: MealSlot) {
    setNotice(null);
    try {
      const { recipe } = await endpoints.rerollMealEntry(dayIndex, slot);
      track('menu_reroll');
      if (!recipe) {
        setNotice('Нет других вариантов');
        return;
      }
      setPlan((p) => (p ? { ...p, entries: p.entries.map((en) => (en.dayIndex === dayIndex && en.slot === slot ? { ...en, recipe } : en)) } : p));
    } catch (e) {
      setError(e instanceof ApiException ? e.body.message : 'Не удалось подобрать другой вариант');
    }
  }

  if (plan === null && !error)
    return (
      <div className="space-y-3 px-4 pt-6">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  if (error)
    return (
      <div className="px-4 pt-6">
        <ErrorState message={error} onRetry={() => void load()} />
      </div>
    );

  // Заполненный план → кнопка-«пересборка» (та же команда, новый случайный seed на бэке). Спека §5/§11.
  const hasEntries = plan!.entries.length > 0;

  return (
    <div className="px-4 pt-6">
      <h1 className="mb-3 text-2xl font-bold text-ink">Меню недели</h1>
      <div className="mb-4 flex gap-2">
        <button
          onClick={() => void generate()}
          disabled={busy}
          className="rounded-xl bg-primary-600 px-4 py-2.5 font-semibold text-white disabled:opacity-60"
        >
          {busy ? 'Собираю…' : hasEntries ? '✨ Пересобрать неделю' : '✨ Собрать неделю'}
        </button>
        <button
          onClick={() => nav('/plan/shopping')}
          className="rounded-xl bg-white px-4 py-2.5 font-semibold text-primary-700 shadow-sm"
        >
          Список покупок
        </button>
      </div>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <span className="text-xs text-ink/50">Уклон меню:</span>
        {GOALS.map((g) => (
          <button
            key={g.key}
            onClick={() => onGoalClick(g.key)}
            disabled={busy}
            aria-pressed={activeGoal === g.key}
            className={`rounded-full px-3 py-1.5 text-sm font-medium disabled:opacity-60 ${
              activeGoal === g.key ? 'bg-primary-600 text-white' : 'bg-white text-primary-700 shadow-sm'
            }`}
          >
            {g.label}
            {locked && ' 🔒'}
          </button>
        ))}
        {activeGoal && (
          <button onClick={() => void generate()} className="rounded-full px-3 py-1.5 text-sm text-ink/50">
            Сбросить ✕
          </button>
        )}
      </div>
      <div className="mb-4">
        <span className="text-xs text-ink/50">Что включить:</span>
        <div className="mt-1 flex flex-wrap gap-2">
          {SLOTS.map(({ slot, label }) => {
            const on = slots.has(slot);
            return (
              <button
                key={slot}
                type="button"
                onClick={() => toggleSlot(slot)}
                aria-pressed={on}
                className={`rounded-full px-3 py-1.5 text-sm font-medium ${
                  on ? 'bg-primary-600 text-white' : 'bg-black/5 text-ink/60'
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>
      {lowVariety && (
        <p className="mb-3 rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-700">
          Вариантов под ваши фильтры маловато — в меню есть повторы.
        </p>
      )}
      {notice && (
        <p className="mb-3 rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-700">{notice}</p>
      )}

      <div className="space-y-5 pb-4">
        {DAYS.map((dayLabel, dayIndex) => (
          <section key={dayIndex}>
            <h2 className="mb-2 text-sm font-semibold text-ink/60">{dayLabel}</h2>
            <div className="space-y-2">
              {SLOTS.filter((s) => slots.has(s.slot)).map(({ slot, label }) => {
                const e = cell(dayIndex, slot);
                return (
                  <div key={slot} className="flex items-center gap-2">
                    <span className="w-28 shrink-0 text-xs text-ink/50">{label}</span>
                    {e ? (
                      <div className="flex flex-1 items-center justify-between rounded-xl bg-white px-3 py-2 shadow-sm">
                        <button className="text-left text-sm text-ink" onClick={() => setPicker({ dayIndex, slot })}>
                          {e.recipe.title ?? ''}
                        </button>
                        <div className="ml-2 flex shrink-0 items-center gap-2">
                          <button
                            aria-label="Другой вариант"
                            className="text-primary-600"
                            onClick={() => void reroll(dayIndex, slot)}
                          >
                            ↻
                          </button>
                          <button
                            aria-label="Убрать"
                            className="text-ink/40"
                            onClick={() => void clearSlot(dayIndex, slot)}
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => setPicker({ dayIndex, slot })}
                        className="flex-1 rounded-xl border border-dashed border-black/15 px-3 py-2 text-left text-sm text-ink/40"
                      >
                        + добавить
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>

      {picker && (
        <SlotPickerModal slot={picker.slot} onPick={(id) => void pick(id)} onClose={() => setPicker(null)} />
      )}
      {upsell && (
        <UpgradeModal requiredTier={upsell.requiredTier} message={upsell.message} onClose={() => setUpsell(null)} />
      )}
    </div>
  );
}
